/**
 * Исполнение расходного договора (ДГ-07, ДГ-13): поставлено и оплачено против договора, остаток к поставке и к
 * оплате, просроченные поставки по графику и неустойка по условиям договора. Суммы — по уровню денег: без него
 * видны количества и дни просрочки. Условия неустойки задаются в проекте договора; после подписания не меняются.
 */
import type { ContractDetail, ContractExecution } from '@epc/contracts';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { ApiError, api } from '../../lib/api';
import { date, tenge } from '../../lib/format';

import { Button } from '../../ui/button';
import { ErrorBox, toast } from '../../ui/feedback';
import { Field, Input } from '../../ui/form';
import { Panel } from '../../ui/layout';

/** Сумма в валюте договора: тенге — как везде, другая валюта — с её кодом */
const sum = (v: number | null, currency: string) =>
  currency === 'KZT'
    ? tenge(v)
    : v === null
      ? '—'
      : `${new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 }).format(v)} ${currency}`;

const fmt = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 3 });
const dec = (s: string) => (s.trim() ? Number(s.replace(/\s/g, '').replace(',', '.')) : null);
const bad = (s: string) => s.trim() !== '' && !Number.isFinite(dec(s));

/** Условия неустойки: в проекте договора — правка, дальше — строка текста */
export function PenaltyTerms({
  d,
  canEdit,
  onSaved,
}: {
  d: ContractDetail;
  canEdit: boolean;
  onSaved: (x: ContractDetail) => void;
}) {
  const [rate, setRate] = useState(d.penaltyRate?.toString() ?? '');
  const [cap, setCap] = useState(d.penaltyCap?.toString() ?? '');
  const save = useMutation({
    mutationFn: () => {
      if (bad(rate) || bad(cap)) throw new Error('Проценты — числом, например 0,1');
      return api<ContractDetail>(`/contracts/${d.id}`, {
        method: 'PATCH',
        body: { penaltyRate: dec(rate), penaltyCap: dec(cap) },
      });
    },
    onSuccess: (x) => {
      onSaved(x);
      toast('Условия неустойки сохранены');
    },
  });
  if (!(canEdit && d.status === 'draft'))
    return d.penaltyRate != null ? (
      <p className="text-[13px] text-ink-2">
        Неустойка за просрочку поставки: {fmt.format(d.penaltyRate)} % в день от стоимости непоставленного
        {d.penaltyCap != null ? `, не больше ${fmt.format(d.penaltyCap)} % суммы договора` : ''}.
      </p>
    ) : null;
  const changed = rate !== (d.penaltyRate?.toString() ?? '') || cap !== (d.penaltyCap?.toString() ?? '');
  return (
    <div className="flex flex-col gap-2">
      <div className="grid gap-2 sm:grid-cols-2">
        <Field
          label="Неустойка за просрочку поставки, % в день"
          hint="от стоимости непоставленного; пусто — нет"
        >
          <Input
            aria-label="Неустойка за просрочку поставки, % в день"
            className="max-md:h-11"
            inputMode="decimal"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
          />
        </Field>
        <Field label="Не больше, % суммы договора" hint="пусто — без предела">
          <Input
            aria-label="Предел неустойки, % суммы договора"
            className="max-md:h-11"
            inputMode="decimal"
            value={cap}
            onChange={(e) => setCap(e.target.value)}
          />
        </Field>
      </div>
      <div>
        <Button
          size="sm"
          variant="secondary"
          disabled={!changed}
          loading={save.isPending}
          onClick={() => save.mutate()}
        >
          Сохранить условия неустойки
        </Button>
      </div>
      <ErrorBox error={save.error} />
    </div>
  );
}

const hidden = (err: unknown) => err instanceof ApiError && (err.status === 403 || err.status === 404);

/** Панель «Исполнение договора» в карточке подписанного расходного договора */
export function ContractExecutionPanel({ contractId }: { contractId: string }) {
  const q = useQuery({
    queryKey: ['contracts', 'execution', contractId],
    queryFn: () => api<ContractExecution>(`/contracts/${contractId}/execution`),
    retry: false,
  });
  if (q.isPending || (q.isError && hidden(q.error))) return null;
  if (q.isError) return <ErrorBox error={q.error} />;
  const e = q.data;
  const money = e.amount !== null;
  return (
    <Panel
      title="Исполнение договора"
      meta={e.overdue.length ? `просрочено поставок: ${e.overdue.length}` : 'просроченных поставок нет'}
    >
      {money ? (
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-[14px] sm:grid-cols-4">
          <div>
            <dt className="text-[12px] text-ink-2">Поставлено</dt>
            <dd className="num">{sum(e.delivered, e.currency)}</dd>
          </div>
          <div>
            <dt className="text-[12px] text-ink-2">К поставке</dt>
            <dd className="num">{sum(e.toDeliver, e.currency)}</dd>
          </div>
          <div>
            <dt className="text-[12px] text-ink-2">Оплачено</dt>
            <dd className="num">{sum(e.paid, e.currency)}</dd>
          </div>
          <div>
            <dt className="text-[12px] text-ink-2">К оплате</dt>
            <dd className="num">{sum(e.toPay, e.currency)}</dd>
          </div>
          {e.currency !== 'KZT' ? (
            <p className="col-span-2 text-[12px] text-ink-2 sm:col-span-4">
              Оплаты учтены в тенге, без пересчёта в валюту договора: «оплачено» и «к оплате» —
              приблизительно.
            </p>
          ) : null}
          {e.overpaid && e.currency === 'KZT' ? (
            <div className="col-span-2 sm:col-span-4">
              <dd className="num text-danger">
                Оплачено сверх суммы договора: {sum(e.overpaid, e.currency)}
              </dd>
            </div>
          ) : null}
        </dl>
      ) : (
        <p className="text-[13px] text-ink-2">Суммы видит сотрудник с доступом к суммам договоров.</p>
      )}
      {e.items.some((i) => i.remaining > 0) ? (
        <ul className="mt-3 divide-y divide-line text-[13px]">
          {e.items
            .filter((i) => i.remaining > 0)
            .map((i) => (
              <li key={`${i.name}|${i.unit}`} className="flex justify-between gap-3 py-2">
                <span className="min-w-0 text-ink">{i.name}</span>
                <span className="num shrink-0 text-ink-2">
                  осталось {fmt.format(i.remaining)} из {fmt.format(i.contracted)} {i.unit}
                </span>
              </li>
            ))}
        </ul>
      ) : null}
      {e.overdue.length ? (
        <div className="mt-3">
          <h3 className="text-[14px] font-medium text-ink">Просроченные поставки</h3>
          <ul className="mt-1 divide-y divide-line text-[13px]">
            {e.overdue.map((o) => (
              <li key={o.rowId} className="flex flex-col gap-0.5 py-2">
                <span className="text-ink">{o.description || 'Поставка по графику'}</span>
                <span className="text-danger">
                  по графику {date(o.plannedDate)}, просрочка {o.days} дн.
                </span>
                {money ? (
                  <span className="num text-ink-2">
                    не поставлено на {sum(o.value, e.currency)}
                    {o.estimated ? ' (оценка: у строки нет позиции спецификации)' : ''}
                    {e.penaltyRate != null ? ` · неустойка ${sum(o.penalty, e.currency)}` : ''}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
          {money && e.penaltyRate != null ? (
            <p className="num mt-2 text-[14px] text-ink">
              Неустойка по договору: {sum(e.penalty, e.currency)}
              {e.penaltyCapped ? ' — достигнут предел по договору' : ''}
            </p>
          ) : null}
          {e.penaltyRate != null ? (
            <p className="mt-1 text-[12px] text-ink-2">
              Неустойка считается от того, что не поставлено сейчас, за все дни просрочки: поставка с
              опозданием её уменьшает.
            </p>
          ) : null}
          {e.penaltyRate == null ? (
            <p className="mt-2 text-[12px] text-ink-2">Условия неустойки в договоре не заданы.</p>
          ) : null}
        </div>
      ) : null}
    </Panel>
  );
}
